import React, { useState, useContext, useEffect } from "react";
import clsx from "clsx";
import moment from "moment";
import {
  makeStyles,
  Avatar,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  MenuItem,
  IconButton,
  Menu,
  Switch,
  useTheme,
  useMediaQuery,
  Button,
  Chip,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { Link } from "react-router-dom";

import MenuIcon from "@material-ui/icons/Menu";
import AccountCircle from "@material-ui/icons/AccountCircle";

import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import NotificationsVolume from "../components/NotificationsVolume";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import DarkMode from "../components/DarkMode";
import { i18n } from "../translate/i18n";
import toastError from "../errors/toastError";
import AnnouncementsPopover from "../components/AnnouncementsPopover";

import logo from "../assets/logo.png";
import { SocketContext } from "../context/Socket/SocketContext";
import ChatPopover from "../pages/Chat/ChatPopover";

import { useDate } from "../hooks/useDate";

import ColorModeContext from "../layout/themeContext";
import Brightness4Icon from '@material-ui/icons/Brightness4';
import Brightness7Icon from '@material-ui/icons/Brightness7';
import LanguageControl from "../components/LanguageControl";
import ConfirmationModal from "../components/ConfirmationModal";
import { versionSystem } from "../../package.json";

const drawerWidth = 299;

const useStyles = makeStyles((theme) => {
  const isDark = theme.mode === "dark";
  return {
    root: {
      display: "flex",
      height: "100vh",
      [theme.breakpoints.down("sm")]: {
        height: "calc(100vh - 56px)",
      },
      backgroundColor: theme.palette.fancyBackground,
      '& .MuiButton-outlinedPrimary': {
        color: '#FFF',
        backgroundColor: isDark ? '#1c1c1c' : theme.palette.primary.main,
      },
      '& .MuiTab-textColorPrimary.Mui-selected': {
        color: isDark ? '#FFF' : 'Primary',
      }
    },
    avatar: {
      width: "100%",
    },
    toolbar: {
      paddingRight: 24,
      minHeight: 56,
      height: 56,
      color: isDark ? "#E2E8F0" : "#1E293B",
      backgroundColor: isDark ? "rgba(30,41,59,0.92)" : "rgba(255,255,255,0.92)",
      backdropFilter: "blur(12px)",
      WebkitBackdropFilter: "blur(12px)",
      borderBottom: isDark ? "1px solid rgba(51,65,85,0.6)" : "1px solid rgba(226,232,240,0.8)",
      boxShadow: isDark
        ? "0 1px 3px rgba(0, 0, 0, 0.3)"
        : "0 1px 3px rgba(15, 23, 42, 0.04)",
      "& .MuiIconButton-root": {
        color: isDark ? "#94A3B8 !important" : "#64748B !important",
        transition: "all 0.2s",
      },
      "& .MuiIconButton-root:hover": {
        backgroundColor: "rgba(5, 150, 105, 0.08)",
        color: "#059669 !important",
      },
    },
    toolbarIcon: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "12px 16px",
      minHeight: 64,
      borderBottom: "1px solid rgba(255,255,255,0.08)",
      [theme.breakpoints.down("sm")]: {
        height: 50
      }
    },
    appBar: {
      zIndex: theme.zIndex.drawer + 1,
      backgroundColor: isDark ? "#1E293B" : "#FFFFFF",
      color: isDark ? "#E2E8F0" : "#1E293B",
      boxShadow: "none",
      borderBottom: isDark ? "1px solid #334155" : "1px solid #E2E8F0",
      transition: theme.transitions.create(["width", "margin"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    },
    appBarShift: {
      marginLeft: drawerWidth,
      width: `calc(100% - ${drawerWidth}px)`,
      transition: theme.transitions.create(["width", "margin"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      [theme.breakpoints.down("sm")]: {
        marginLeft: 0,
        width: "100%"
      }
    },
    menuButton: {
      marginRight: 36,
    },
    menuButtonHidden: {
      display: "none",
    },
    title: {
      flexGrow: 1,
      fontSize: 14,
      color: isDark ? "rgba(255, 255, 255, 0.87)" : "rgba(0, 0, 0, 0.87)",
    },
    drawerPaper: {
      position: "relative",
      whiteSpace: "nowrap",
      width: drawerWidth,
      height: "100%",
      backgroundColor: "#0F172A",
      borderRight: "none",
      color: "#E2E8F0",
      overflowX: "hidden",
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      [theme.breakpoints.down("sm")]: {
        width: 280
      },
      ...theme.scrollbarStylesSoft
    },
    drawerToolbar: {
      "& .MuiIconButton-root": {
        color: "#94A3B8",
        "&:hover": {
          color: "#E2E8F0",
          backgroundColor: "rgba(255,255,255,0.08)",
        },
      },
    },
    drawerPaperClose: {
      overflowX: "hidden",
      backgroundColor: "#0F172A",
      borderRight: "none",
      color: "#E2E8F0",
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: theme.spacing(7),
      [theme.breakpoints.up("sm")]: {
        width: theme.spacing(9),
      },
      [theme.breakpoints.down("sm")]: {
        width: 0,
        display: "none"
      }
    },
    appBarSpacer: {
      minHeight: 56,
    },
    content: {
      flex: 1,
      width: "100%",
      maxWidth: "100%",
      overflow: "auto",
      overflowX: "hidden",
      minHeight: 0,
      WebkitOverflowScrolling: "touch",
    },
    container: {
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(4),
    },
    paper: {
      padding: theme.spacing(2),
      display: "flex",
      overflow: "auto",
      flexDirection: "column"
    },
    containerWithScroll: {
      flex: 1,
      minHeight: 0,
      padding: 0,
      overflowY: "auto",
      overflowX: "hidden",
      ...theme.scrollbarStyles,
    },
    drawerFooter: {
      flexShrink: 0,
      padding: theme.spacing(1.5, 2),
      borderTop: "1px solid rgba(255, 255, 255, 0.08)",
      backgroundColor: "rgba(0, 0, 0, 0.15)",
    },
    drawerFooterUser: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.25),
      marginBottom: theme.spacing(0.5),
    },
    drawerFooterRole: {
      fontSize: "0.75rem",
      color: "#94A3B8",
      marginBottom: 0,
    },
    drawerFooterVersion: {
      fontSize: "0.625rem",
      color: "#64748B",
      marginTop: theme.spacing(0.5),
      opacity: 0.7,
    },
    NotificationsPopOver: {
      // color: theme.barraSuperior.secondary.main,
    },
    logo: {
      width: "80%",
      height: "auto",
      maxWidth: 180,
      [theme.breakpoints.down("sm")]: {
        width: "auto",
        height: "80%",
        maxWidth: 180,
      },
      logo: theme.logo
    },
  };
});

const LoggedInLayout = ({ children, themeToggle }) => {
  const classes = useStyles();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading } = useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(() => {
    const saved = localStorage.getItem("drawerOpen");
    if (saved !== null) return saved === "true";
    return true;
  });
  const [drawerVariant, setDrawerVariant] = useState("permanent");
  const { user } = useContext(AuthContext);

  const theme = useTheme();
  const isDark = theme.mode === "dark";
  const { colorMode } = useContext(ColorModeContext);
  const greaterThenSm = useMediaQuery(theme.breakpoints.up("sm"));

  const [volume, setVolume] = useState(localStorage.getItem("volume") || 1);
  const [attendancePaused, setAttendancePaused] = useState(
    () => localStorage.getItem("attendancePaused") === "true"
  );
  const [showPauseConfirmDialog, setShowPauseConfirmDialog] = useState(false);

  const { dateToClient } = useDate();

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    if (document.body.offsetWidth < 600) {
      setDrawerVariant("temporary");
    } else {
      setDrawerVariant("permanent");
    }
  }, [drawerOpen]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const userId = localStorage.getItem("userId");

    const socket = socketManager.getSocket(companyId);

    socket.on(`company-${companyId}-auth`, (data) => {
      if (data.user.id === +userId) {
        toastError("Sua conta foi acessada em outro computador.");
        setTimeout(() => {
          localStorage.clear();
          window.location.reload();
        }, 1000);
      }
    });

    socket.emit("userStatus");
    const interval = setInterval(() => {
      socket.emit("userStatus");
    }, 1000 * 60 * 5);

    return () => {
      socket.disconnect();
      clearInterval(interval);
    };
  }, [socketManager]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const drawerClose = () => {
    if (document.body.offsetWidth < 600) {
      setDrawerOpen(false);
    }
  };

  const handleAttendancePausedChange = (event) => {
    const wantToPause = !event.target.checked;
    if (wantToPause) {
      setShowPauseConfirmDialog(true);
    } else {
      setAttendancePaused(false);
      localStorage.setItem("attendancePaused", "false");
    }
  };

  const handleConfirmPause = () => {
    setAttendancePaused(true);
    localStorage.setItem("attendancePaused", "true");
    setShowPauseConfirmDialog(false);
  };

  const handleMenuItemClick = () => {
    const { innerWidth: width } = window;
    if (width <= 600) {
      setDrawerOpen(false);
    }
  };

  const toggleColorMode = () => {
    colorMode.toggleColorMode();
  }

  if (loading) {
    return <BackdropLoading />;
  }

  const profileLabel = user?.profile === "admin"
    ? "Administrador"
    : user?.profile === "user"
      ? "Usuário"
      : (user?.profile || "Perfil");
  const companyLabel = user?.company?.name || user?.companyName || user?.tenantName || "Empresa";

  // Theme-responsive colors for toolbar inline styles
  const toolbarIconColor = isDark ? "#94A3B8" : "rgba(0, 0, 0, 0.54)";
  const toolbarTextColor = isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)";
  const chipStyle = {
    marginLeft: 8,
    marginRight: 8,
    maxWidth: 260,
    minWidth: 140,
    color: isDark ? "#E2E8F0" : "rgba(0,0,0,0.78)",
    borderColor: isDark ? "rgba(16, 185, 129, 0.4)" : "rgba(5, 150, 105, 0.35)",
    backgroundColor: isDark ? "rgba(16, 185, 129, 0.12)" : "rgba(5, 150, 105, 0.08)",
    fontWeight: 700,
    flexShrink: 0,
  };

  return (
    <div className={classes.root}>
      <Drawer
        variant={drawerVariant}
        className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
        classes={{
          paper: clsx(
            classes.drawerPaper,
            !drawerOpen && classes.drawerPaperClose
          ),
        }}
        open={drawerOpen}
      >
        <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
          <div className={clsx(classes.toolbarIcon, classes.drawerToolbar)}>
            <img src={logo} className={classes.logo} alt="logo" />
          </div>
          <Divider style={{ backgroundColor: "rgba(255,255,255,0.06)" }} />
          <List className={classes.containerWithScroll}>
            <MainListItems drawerClose={drawerClose} />
          </List>
          {drawerOpen && (
            <div className={classes.drawerFooter}>
              <div className={classes.drawerFooterUser}>
                <Avatar style={{ width: 36, height: 36, backgroundColor: "#059669", boxShadow: "0 2px 8px rgba(5, 150, 105, 0.35)" }}>
                  {user?.name ? user.name.charAt(0).toUpperCase() : "?"}
                </Avatar>
                <Typography variant="body2" style={{ fontWeight: 600, color: "#E2E8F0" }} noWrap>
                  {user?.name || "-"}
                </Typography>
              </div>
              <Typography className={classes.drawerFooterRole}>
                {user?.profile === "admin" ? "Administrador" : user?.profile === "user" ? "Usuário" : user?.profile || "-"}
              </Typography>
              <Typography className={classes.drawerFooterVersion} component="div">
                v{versionSystem}
              </Typography>
            </div>
          )}
        </div>
      </Drawer>
      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />
      <ConfirmationModal
        open={showPauseConfirmDialog}
        onClose={() => setShowPauseConfirmDialog(false)}
        onConfirm={handleConfirmPause}
        title={i18n.t("mainDrawer.appBar.pauseAttendance.title")}
        confirmText={i18n.t("mainDrawer.appBar.pauseAttendance.confirm")}
        cancelText={i18n.t("mainDrawer.appBar.pauseAttendance.cancel")}
      >
        {i18n.t("mainDrawer.appBar.pauseAttendance.message")}
      </ConfirmationModal>
      <AppBar
        position="absolute"
        className={clsx(classes.appBar, drawerOpen && classes.appBarShift)}
        color="default"
      >
        <Toolbar variant="dense" className={classes.toolbar}>
          <IconButton
            edge="start"
            aria-label="menu"
            onClick={() => {
              const next = !drawerOpen;
              setDrawerOpen(next);
              localStorage.setItem("drawerOpen", String(next));
            }}
            className={classes.menuButton}
          >
            <MenuIcon />
          </IconButton>

          <Typography component="div" className={classes.title} style={{ flex: 1 }}/>

          <LanguageControl />

          <Chip
            size="small"
            variant="outlined"
            label={`${profileLabel} | ${companyLabel}`}
            style={chipStyle}
          />

          <IconButton onClick={toggleColorMode}>
            {isDark ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>

          <NotificationsVolume
            setVolume={setVolume}
            volume={volume}
          />

          {user.id && <NotificationsPopOver volume={volume} />}

          <AnnouncementsPopover />

          <ChatPopover />

          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Typography variant="body2" style={{ fontSize: 12, color: toolbarTextColor }}>
              {attendancePaused ? "Pausado" : "Ativo"}
            </Typography>
            <Switch
              checked={!attendancePaused}
              onChange={handleAttendancePausedChange}
              color="primary"
              size="small"
            />
          </div>

          <div>
            <IconButton
              aria-label="conta"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={menuOpen}
              onClose={handleCloseMenu}
            >
              <MenuItem onClick={handleOpenUserModal}>
                {i18n.t("mainDrawer.appBar.user.profile")}
              </MenuItem>
              <MenuItem onClick={handleClickLogout}>
                {i18n.t("mainDrawer.appBar.user.logout")}
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />

        {user?.finance?.delinquent && (
          <Alert
            severity="warning"
            variant="outlined"
            style={{ margin: "0 16px 16px", alignItems: "center" }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 12,
                width: "100%",
              }}
            >
              <span style={{ flex: 1, minWidth: 200 }}>
                {i18n.t("finance.banner.message")}
              </span>
              <Button
                component={Link}
                to="/financeiro"
                variant="contained"
                color="primary"
                size="small"
              >
                {i18n.t("finance.banner.action")}
              </Button>
            </div>
          </Alert>
        )}

        {children ? children : null}
      </main>
    </div>
  );
};

export default LoggedInLayout;
